import { isCanceledOrDeleted } from "@stores/purchases/utils";
import { PropsWithChildren, ReactElement } from "react";
import { Box, Card, Flex, useMantineTheme } from "@mantine/core";
import DisabledContainer from "@common/components/layout/DisabledContainer";
import { getPurchaseCardId } from "../utils";
import { IPurchase } from "@stores/purchases/types";
import { useGetHeaderProperties } from "@components/navigation/HeaderNav/hooks";

export function PurchaseCardHeader({
  title,
  titleExtra,
  headerExtra,
}: {
  title: React.ReactNode;
  titleExtra?: React.ReactNode;
  headerExtra?: React.ReactNode;
}) {
  return (
    <Flex
      data-testid="PurchaseCard_header"
      align="baseline"
      direction={{ base: "column", sm: "row" }}
      gap="0.25rem"
    >
      <Flex w="100%" align="baseline">
        {title}
        {titleExtra}
      </Flex>
      {headerExtra && <Box ml={{ sm: "auto" }}>{headerExtra}</Box>}
    </Flex>
  );
}

export default function PurchaseCard({
  purchase,
  children,
}: PropsWithChildren<{ purchase: IPurchase }>): ReactElement {
  const theme = useMantineTheme();
  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);

  // header
  const { headerHeight } = useGetHeaderProperties();
  const headerHeightWithOffset = headerHeight + theme.spacing.md;

  return (
    <DisabledContainer
      disabled={isPurchaseCanceledOrDeleted}
      data-testid="PurchaseCard"
    >
      <Card
        id={getPurchaseCardId(purchase)}
        data-testid={`PurchaseCard_${getPurchaseCardId(purchase)}`}
        withBorder
        px={2.25 * theme.spacing.md}
        py="xl"
        maw={theme.breakpoints.sm}
        // Offset native #hash deep-link scroll so the card clears the sticky header.
        sx={{ scrollMarginTop: headerHeightWithOffset }}
      >
        {children}
      </Card>
    </DisabledContainer>
  );
}
